import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { SUPPLIER_FIELDS, SUPPLIER_SEARCH_COUNTRIES } from '@norum/contracts';
import { appPath } from '@/api/client';
import { useRecordPost } from '@/api/RecordContext';
import { failureOf, said } from '@/api/errors';
import type { WireSupplierCandidate } from '@/api/wire';
import { useMeOrThrow } from '@/app/MeContext';
import { Dialog } from '@/ui/Dialog';
import { Select, Textarea } from '@/ui/fields';
import { Button, Field, Kicker, TextInput } from '@/ui/primitives';
import { Table } from '@/ui/records';
import { useToast } from '@/ui/Toast';
import { useWho } from '@/ui/who';
import { countryName } from '@/ui/words';
import { explainCandidate } from './explain';

/**
 * Finding manufacturers, screening them and choosing a main and a backup
 * (ADR-0162): ask Nia to search a country, add one by hand from a page, read
 * each candidate, shortlist it, have the email prepared in your own Gmail,
 * and choose. Every decision is a person's; nothing here writes to a
 * manufacturer.
 */

const P = {
  research: appPath('/v1/suppliers/research'),
  candidates: appPath('/v1/suppliers/candidates'),
  add: appPath('/v1/suppliers/candidates/add'),
  contact: appPath('/v1/suppliers/contacts/add'),
  decide: appPath('/v1/suppliers/decide'),
  prepare: appPath('/v1/suppliers/prepare-email'),
  contacted: appPath('/v1/suppliers/contacted'),
  automations: appPath('/v1/automations/list'),
  automate: appPath('/v1/automations/decide'),
};

interface CandidatesWire {
  candidates: WireSupplierCandidate[];
  compared: number;
  minCompared: number;
}

type Kind = 'shortlist' | 'unshortlist' | 'reject' | 'main' | 'backup' | 'release';

const STAGE_CHIP: Record<WireSupplierCandidate['stage'], { label: string; tone: 'warning' | 'accent' | 'neutral' | 'danger' }> = {
  candidate: { label: 'FOUND · NOT READ', tone: 'warning' },
  screened: { label: 'ON THE LIST', tone: 'neutral' },
  shortlisted: { label: 'SHORTLISTED', tone: 'accent' },
  contacted: { label: 'WRITTEN TO', tone: 'accent' },
  rejected: { label: 'REJECTED', tone: 'danger' },
};

const NEEDS_WHY: Kind[] = ['reject', 'main', 'backup'];
const DECISION_WORDS: Record<Kind, { title: string; button: string; ask: string }> = {
  shortlist: { title: 'Shortlist', button: 'Shortlist it', ask: '' },
  unshortlist: { title: 'Take off the shortlist', button: 'Take it off', ask: '' },
  reject: { title: 'Reject', button: 'Reject it', ask: 'Why? In a few words: it is kept with your decision.' },
  main: { title: 'Choose as the main manufacturer', button: 'Choose as main', ask: 'Why this one? It is kept with your decision.' },
  backup: { title: 'Choose as the backup', button: 'Choose as backup', ask: 'Why this one as the backup? It is kept with your decision.' },
  release: { title: 'Release', button: 'Release it', ask: '' },
};

export function Manufacturers() {
  const post = useRecordPost();
  const me = useMeOrThrow();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const who = useWho();
  const q = useQuery<CandidatesWire>({ queryKey: ['supplier-candidates', me.department], queryFn: () => post(P.candidates, {}) });
  const mayAsk = me.isSuperadmin || me.leads.includes(me.department);
  const [country, setCountry] = useState<string>('LT');
  const [adding, setAdding] = useState(false);
  const [deciding, setDeciding] = useState<{ c: WireSupplierCandidate; kind: Kind } | null>(null);
  const [contactFor, setContactFor] = useState<WireSupplierCandidate | null>(null);
  const [prepared, setPrepared] = useState<{ c: WireSupplierCandidate; subject: string; body: string; to: string } | null>(null);
  const done = () => {
    void qc.invalidateQueries({ queryKey: ['supplier-candidates'] });
    void qc.invalidateQueries({ queryKey: ['suppliers'] });
    void qc.invalidateQueries({ queryKey: ['supplier-compare'] });
  };
  const failed = (e: unknown) => toast({ text: said(failureOf(e)), tone: 'danger' });

  const search = useMutation({
    mutationFn: () => post<{ routing: { outcome: string; reason?: string }; country: string }>(P.research, { code: country }),
    onSuccess: (r) => {
      if (r.routing.outcome === 'refused') {
        toast({ text: `Executive cannot search yet: ${r.routing.reason?.replace('needs_grant:', 'it does not hold ') ?? 'it was refused'}. A superadmin grants it (setup step “Find manufacturers”).`, tone: 'danger' });
      } else {
        toast({ text: `Asked. Nia searches ${r.country} and reads each maker's own site; the candidates and a task for you arrive when she has finished.` });
      }
    },
    onError: failed,
  });
  const decide = useMutation({
    mutationFn: (input: { supplierId: string; kind: Kind; reason?: string }) => post(P.decide, input),
    onSuccess: done,
    onError: failed,
  });
  const prepare = useMutation({
    mutationFn: (c: WireSupplierCandidate) => post<{ subject: string; body: string; to: string }>(P.prepare, { supplierId: c.id }).then((r) => ({ ...r, c })),
    onSuccess: (r) => setPrepared(r),
    onError: failed,
  });
  const sent = useMutation({
    mutationFn: (supplierId: string) => post(P.contacted, { supplierId }),
    onSuccess: () => {
      done();
      toast({ text: 'Noted: it is written to. Its answers are what you hear back, confirmed in writing.' });
    },
    onError: failed,
  });

  const data = q.data;
  const rows = data?.candidates ?? [];
  const shortlisted = data?.compared ?? 0;
  const min = data?.minCompared ?? 3;
  const busy = decide.isPending || prepare.isPending || sent.isPending;

  return (
    <section className="flex flex-col gap-3" data-testid="manufacturers">
      <Kicker>Find manufacturers</Kicker>
      <p className="m-0 max-w-[68ch] text-dense text-secondary">
        Ask Nia to look for makers of fermented garlic and onion in a country. She reads each maker’s own site and keeps only what the page says, with the words she found it in. You read
        each one, shortlist the ones worth writing to, and choose a main and a backup from at least {min}.
      </p>
      <AutomationOffer mayDecide={mayAsk} />
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Search in">
          <Select aria-label="Country to search" value={country} onChange={(e) => setCountry(e.target.value)} options={SUPPLIER_SEARCH_COUNTRIES.map((c) => ({ value: c, label: countryName(c) }))} />
        </Field>
        {mayAsk && (
          <Button variant="primary" disabled={search.isPending} onClick={() => search.mutate()}>
            Ask Nia to search
          </Button>
        )}
        <Button onClick={() => setAdding((a) => !a)}>{adding ? 'Close' : 'Add one by hand…'}</Button>
      </div>
      {adding && <AddByHand onDone={() => { setAdding(false); done(); }} />}
      {data && (
        <p className="m-0 text-dense text-secondary" data-testid="compared">
          {shortlisted >= min
            ? `${shortlisted} are on the shortlist: you can choose a main and a backup.`
            : `${shortlisted} of ${min} needed are on the shortlist. Shortlist ${min - shortlisted} more to compare before choosing.`}
        </p>
      )}
      <Table<WireSupplierCandidate>
        title="manufacturers found"
        testId="candidates"
        blurb="Every maker on the list, found by Nia or added by hand, and how far it has got."
        rows={rows}
        onOpen={(c) => void navigate(`/suppliers/${c.id}`)}
        explain={(c) =>
          explainCandidate(c, who, {
            department: me.department,
            mayDecide: mayAsk,
            humanId: me.humanId,
            compared: shortlisted,
            minCompared: min,
            actions: {
              busy,
              shortlist: () => decide.mutate({ supplierId: c.id, kind: 'shortlist' }),
              unshortlist: () => decide.mutate({ supplierId: c.id, kind: 'unshortlist' }),
              reject: () => setDeciding({ c, kind: 'reject' }),
              main: () => setDeciding({ c, kind: 'main' }),
              backup: () => setDeciding({ c, kind: 'backup' }),
              release: () => setDeciding({ c, kind: 'release' }),
              prepare: () => prepare.mutate(c),
              sent: () => sent.mutate(c.id),
              addContact: () => setContactFor(c),
            },
          })
        }
        emptyTitle="No manufacturer yet"
        emptyBody="Ask Nia to search a country, or add one you found from its own page."
        columns={[
          { label: 'Manufacturer', get: (c) => c.name, primary: true },
          { label: 'Country', get: (c) => countryName(c.country) },
          { label: 'Stage', get: (c) => (c.selection ? (c.selection === 'main' ? 'Main' : 'Backup') : STAGE_CHIP[c.stage].label), chip: (c) => (c.selection ? { label: c.selection === 'main' ? 'MAIN' : 'BACKUP', tone: 'accent' as const } : STAGE_CHIP[c.stage]) },
          { label: 'Answers read', get: (c) => `${c.answered} of ${SUPPLIER_FIELDS.length}` },
          { label: 'Contact', get: (c) => (c.hasContact ? (c.contactKind === 'role' ? 'Shared address' : 'A named person') : '—') },
        ]}
        facets={[
          { label: 'Stage', get: (c) => (c.selection ? (c.selection === 'main' ? 'Main' : 'Backup') : STAGE_CHIP[c.stage].label) },
          { label: 'Country', get: (c) => countryName(c.country) },
        ]}
      />
      {deciding && (
        <DecideDialog
          key={`${deciding.c.id}:${deciding.kind}`}
          c={deciding.c}
          kind={deciding.kind}
          busy={decide.isPending}
          onClose={() => setDeciding(null)}
          onDecide={(reason) =>
            decide.mutate(
              { supplierId: deciding.c.id, kind: deciding.kind, ...(reason ? { reason } : {}) },
              {
                onSuccess: () => {
                  toast({ text: deciding.kind === 'main' || deciding.kind === 'backup' ? `${deciding.c.name} is the ${deciding.kind} manufacturer.` : 'Done.' });
                  setDeciding(null);
                },
              },
            )
          }
        />
      )}
      {contactFor && <ContactDialog c={contactFor} onClose={() => setContactFor(null)} onDone={() => { setContactFor(null); done(); }} />}
      {prepared && (
        <Dialog
          open
          onOpenChange={(o) => !o && setPrepared(null)}
          kicker={`Email · ${prepared.c.name}`}
          title="It is in your Gmail drafts"
          note="Your twin put it there. Read it in Gmail, change what you like, and send it yourself: the platform never sends it. When you have, press “I sent it”."
          primary={{
            label: 'I sent it',
            onClick: () => {
              sent.mutate(prepared.c.id);
              setPrepared(null);
            },
          }}
          secondary={{ label: 'Close', onClick: () => setPrepared(null) }}
        >
          <div className="flex flex-col gap-2 p-6" data-testid="prepared-email">
            <span className="text-dense text-meta">To {prepared.to}</span>
            <strong className="text-body">{prepared.subject}</strong>
            <pre className="m-0 whitespace-pre-wrap font-sans text-dense text-secondary">{prepared.body}</pre>
          </div>
        </Dialog>
      )}
    </section>
  );
}

function DecideDialog({ c, kind, busy, onClose, onDecide }: { c: WireSupplierCandidate; kind: Kind; busy: boolean; onClose: () => void; onDecide: (reason: string | null) => void }) {
  const words = DECISION_WORDS[kind];
  const needsWhy = NEEDS_WHY.includes(kind);
  const [reason, setReason] = useState('');
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker={c.name}
      title={words.title}
      danger={kind === 'reject'}
      primary={{ label: words.button, disabled: busy || (needsWhy && reason.trim().length < 3), onClick: () => onDecide(needsWhy ? reason.trim() : null) }}
      secondary={{ label: 'Not now', onClick: onClose }}
    >
      <div className="flex flex-col gap-3 p-6">
        {needsWhy && (
          <Field label={words.ask}>
            <Textarea aria-label="Why" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        )}
        {kind === 'main' && <p className="m-0 text-dense text-secondary">If another maker is the main, it steps down and is told so in the record.</p>}
      </div>
    </Dialog>
  );
}

function ContactDialog({ c, onClose, onDone }: { c: WireSupplierCandidate; onClose: () => void; onDone: () => void }) {
  const post = useRecordPost();
  const toast = useToast();
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [address, setAddress] = useState('');
  const [page, setPage] = useState('');
  const add = useMutation({
    mutationFn: () => post(P.contact, { supplierId: c.id, address: address.trim(), sourceUrl: page.trim(), ...(name.trim() ? { contactName: name.trim() } : {}), ...(role.trim() ? { contactRole: role.trim() } : {}) }),
    onSuccess: () => {
      toast({ text: 'Added, sealed, with the page it is written on.' });
      onDone();
    },
    onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker={c.name}
      title="Add a contact"
      note="The sales or partnerships person, from a page that shows them. The address is kept sealed; the page is kept so anyone can check it."
      primary={{ label: 'Add the contact', disabled: add.isPending || !address.trim() || !page.trim(), onClick: () => add.mutate() }}
      secondary={{ label: 'Not now', onClick: onClose }}
    >
      <div className="flex flex-col gap-3 p-6">
        <Field label="Name (if the page gives one)">
          <TextInput aria-label="Contact name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Role (if the page gives one)">
          <TextInput aria-label="Contact role" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Export manager" />
        </Field>
        <Field label="Email address">
          <TextInput aria-label="Contact address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="sales@maker.example" />
        </Field>
        <Field label="The page it is written on">
          <TextInput aria-label="Page the contact is on" value={page} onChange={(e) => setPage(e.target.value)} placeholder="https://" />
        </Field>
      </div>
    </Dialog>
  );
}

function AddByHand({ onDone }: { onDone: () => void }) {
  const post = useRecordPost();
  const toast = useToast();
  const [name, setName] = useState('');
  const [country, setCountry] = useState('LT');
  const [page, setPage] = useState('');
  const add = useMutation({
    mutationFn: () => post<{ existing: boolean }>(P.add, { name: name.trim(), country, sourceUrl: page.trim() }),
    onSuccess: (r) => {
      toast({ text: r.existing ? 'That maker is on the list already.' : 'Added. Read it, then shortlist it or reject it.' });
      onDone();
    },
    onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
  });
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="add-manufacturer">
      <Field label="Maker">
        <TextInput aria-label="Manufacturer name" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Its country">
        <Select aria-label="Manufacturer country" value={country} onChange={(e) => setCountry(e.target.value)} options={['PL', 'LT', 'ES', 'UA', 'RS', 'HR', 'BA', 'BG', 'RO', 'MK', 'AL', 'ME', 'SI', 'XK'].map((c) => ({ value: c, label: countryName(c) }))} />
      </Field>
      <Field label="Its own page">
        <TextInput aria-label="Page it is on" value={page} onChange={(e) => setPage(e.target.value)} placeholder="https://" />
      </Field>
      <Button disabled={add.isPending || name.trim().length < 2 || !page.trim()} onClick={() => add.mutate()}>
        Add it
      </Button>
    </div>
  );
}

interface AutomationsWire {
  runs: number;
  threshold: number;
  offers: Array<{ process: string; state: 'proposed' | 'on' | 'declined' | 'off'; countries: string[]; runsSeen: number }>;
}

/**
 * The count of manufacturers taken all the way by hand, and what is offered
 * at ten (ADR-0163). Everyone sees the count; only the lead or a superadmin
 * sees the buttons. It is never on until they press it.
 */
function AutomationOffer({ mayDecide }: { mayDecide: boolean }) {
  const post = useRecordPost();
  const me = useMeOrThrow();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery<AutomationsWire>({ queryKey: ['automations', me.department], queryFn: () => post(P.automations, {}) });
  const [countries, setCountries] = useState<string[]>([]);
  const decide = useMutation({
    mutationFn: (input: { kind: 'turn_on' | 'decline' | 'turn_off'; countries?: string[] }) => post(P.automate, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['automations'] }),
    onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
  });
  const data = q.data;
  if (!data) return null;
  const offer = data.offers[0];
  if (!offer) {
    return (
      <p className="m-0 max-w-[68ch] text-dense text-secondary" data-testid="process-count">
        {data.runs} of {data.threshold} manufacturers taken all the way by hand (added, read, written to). At {data.threshold} the platform offers to do the searching itself.
      </p>
    );
  }
  if (offer.state === 'declined') return null;
  return (
    <div className="max-w-[68ch] rounded-md border border-line p-3 text-dense" data-testid="process-offer">
      {offer.state === 'on' ? (
        <>
          <p className="m-0">The monthly search is on for {offer.countries.map(countryName).join(', ')}. It adds candidates and a task for you; it never writes to anyone.</p>
          {mayDecide && <Button disabled={decide.isPending} onClick={() => decide.mutate({ kind: 'turn_off' })}>Turn it off</Button>}
        </>
      ) : (
        <>
          <p className="m-0">
            {offer.state === 'off' ? 'The monthly search is off.' : `${offer.runsSeen} manufacturers have been taken all the way by hand.`} The platform can search for new makers every month and
            put the candidates and a task for you on your day. It will not draft or send anything.
          </p>
          {mayDecide ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {SUPPLIER_SEARCH_COUNTRIES.map((c) => (
                <label key={c} className="flex items-center gap-1">
                  <input type="checkbox" checked={countries.includes(c)} onChange={(e) => setCountries((cs) => (e.target.checked ? [...cs, c] : cs.filter((x) => x !== c)))} />
                  {countryName(c)}
                </label>
              ))}
              <Button variant="primary" disabled={decide.isPending || countries.length === 0} onClick={() => decide.mutate({ kind: 'turn_on', countries })}>Turn on</Button>
              {offer.state === 'proposed' && <Button disabled={decide.isPending} onClick={() => decide.mutate({ kind: 'decline' })}>Not now</Button>}
            </div>
          ) : (
            <p className="m-0 mt-1 text-secondary">The department’s lead decides.</p>
          )}
        </>
      )}
    </div>
  );
}
